/* ============================================================
   FA Fulfillment Selector — fa-fulfillment.js

   Sep 2026: inventory badge now reads real RFMS quantities from
   the #fa-inventory-data JSON block (written by the Liquid from
   rfms.on_hand_* variant metafields) and bands large numbers as
   "Plenty In Stock" above a per-unit threshold.

   GRACEFUL FALLBACK: if the JSON block is missing, or a variant
   has no RFMS metafields yet, the badge uses the (capped) Shopify
   inventory_quantity exactly as before. Nothing breaks on
   unsynced products.
   ============================================================ */
(function () {
  'use strict';

  var LS_KEY   = 'fa_fulfil_mode';
  var LS_ZIP   = 'fa_fulfil_zip';
  var LS_STATE = 'fa_fulfil_state';

  /* Parsed #fa-inventory-data payload (null when absent/invalid) */
  var invData = null;

  function getSaved() {
    try { return localStorage.getItem(LS_KEY) || 'ship'; } catch (e) { return 'ship'; }
  }

  function save(mode) {
    try { localStorage.setItem(LS_KEY, mode); } catch (e) {}
  }

  /* ── Pick unit word(s) for the inventory badge based on sell-by ──
     Returns an object so we can have different singular/plural forms
     and an "omit unit entirely" mode (for `each` and remnants where the
     unit word is meaningless or implied).
  */
  function getUnitInfo(sellBy) {
    sellBy = (sellBy || '').toLowerCase().trim();

    /* By box */
    if (sellBy === 'box' || sellBy === 'carpet tile (box)') {
      return { singular: 'Box',   plural: 'Boxes' };
    }
    /* By square foot */
    if (sellBy === 'sqft') {
      return { singular: 'SQ Foot',  plural: 'SQ Feet' };
    }
    /* By square yard (legacy + carpet roll) */
    if (sellBy === 'square yard' || sellBy === 'carpet roll (variable price)') {
      return { singular: 'SQ Yard',  plural: 'SQ Yards' };
    }
    /* Remnant — unique pieces. No unit word reads cleanest ("1 left"). */
    if (sellBy === 'carpet remnant (set price)') {
      return { singular: '',      plural: '' };
    }
    /* Fallback: each / unrecognized — also omit the unit word */
    return { singular: '', plural: '' };
  }

  /* ── Threshold lookup ──
     All three bands, per sell-by unit, from the Liquid JSON:
       { plenty: N, low: N, verylow: N }
     Also tolerates the older format where the value was a bare
     number (the plenty threshold), in case the snippet and this
     file are updated at different moments. The fallbacks here only
     apply if the JSON block is missing entirely. */
  function getBands(sellBy) {
    sellBy = (sellBy || '').toLowerCase().trim();
    var fallback = { plenty: 100, low: 10, verylow: 5 };
    var t = invData && invData.thresholds;
    if (!t) return fallback;
    var v = Object.prototype.hasOwnProperty.call(t, sellBy) ? t[sellBy] : t['default'];
    if (typeof v === 'number') return { plenty: v, low: 10, verylow: 5 };
    if (v && typeof v === 'object') {
      return {
        plenty:  typeof v.plenty  === 'number' ? v.plenty  : fallback.plenty,
        low:     typeof v.low     === 'number' ? v.low     : fallback.low,
        verylow: typeof v.verylow === 'number' ? v.verylow : fallback.verylow
      };
    }
    return fallback;
  }

  /* ── Resolve the quantity to display for a variant ──
     Prefers the RFMS converted figure (real number, in sell-by units);
     falls back to the capped Shopify quantity. Returns an integer. */
  function resolveQty(variantId, shopifyQty) {
    if (invData && invData.variants && variantId != null) {
      var v = invData.variants[String(variantId)];
      if (v) {
        if (typeof v.converted === 'number' && isFinite(v.converted)) {
          return Math.floor(v.converted);
        }
        /* Raw without a conversion is in RFMS units, which may not be
           sell-by units — only trust it when no conversion is needed
           (unit missing or matches nothing to convert). Safer to fall
           back to Shopify's number than to show 4,000 sq ft as boxes. */
        if (typeof v.shopify_qty === 'number') return v.shopify_qty;
      }
    }
    return shopifyQty;
  }

  /* Build the inventory label text. Handles singular/plural, the
     no-unit case (where we just show the number), and all three
     bands. Band boundaries come from getBands() — per sell-by unit,
     editable at the top of fa-fulfillment-selector.liquid. */
  function buildLabel(qty, unit, bands) {
    var u = qty === 1 ? unit.singular : unit.plural;
    var withUnit = u ? (qty.toLocaleString('en-US') + ' ' + u) : ('' + qty.toLocaleString('en-US'));

    if (qty <= 0) return 'Out of Stock';
    if (qty === 1 && u === 'Box') return '1 Box Left \u2014 Order Soon!';
    if (qty === 1) return withUnit + ' Left' + (u ? '' : ' \u2014 Last One');
    if (qty <= bands.verylow) return withUnit + ' Left \u2014 Very Low Stock';
    if (qty <= bands.low)     return withUnit + ' Left \u2014 Low Stock';
    if (qty >= bands.plenty)  return 'Plenty In Stock';
    return withUnit + ' In Stock';
  }

  /* ── Inventory badge ── */
  function updateInventoryBadge(qty, variantId) {
    /* Pull sell-by from the wrapper. If multiple selectors exist (PDP +
       cart summary), they should all share the same product context. */
    var wrap = document.querySelector('.fa-fulfil-selector[data-sell-by]');
    var sellBy = wrap ? wrap.getAttribute('data-sell-by') : '';
    var unit = getUnitInfo(sellBy);

    var effectiveQty = resolveQty(variantId, qty);
    var bands = getBands(sellBy);

    var dotClass = effectiveQty <= bands.verylow ? 'red' : effectiveQty <= bands.low ? 'amber' : 'green';
    var label = buildLabel(effectiveQty, unit, bands);

    document.querySelectorAll('.fa-fulfil__inventory').forEach(function (b) {
      b.innerHTML = '<span class="fa-fulfil__inv-dot ' + dotClass + '"></span><span>' + label + '</span>';
    });

    /* Paint the whole pickup card to match the badge (CSS keys off
       fa-inv--green / fa-inv--amber / fa-inv--red). */
    document.querySelectorAll('.fa-fulfil__pickup-panel').forEach(function (p) {
      p.classList.remove('fa-inv--green', 'fa-inv--amber', 'fa-inv--red');
      p.classList.add('fa-inv--' + dotClass);
    });
  }

  /* ── Update Shopify cart attribute ── */
  function updateCartAttribute(mode) {
    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { fulfillment_method: mode === 'pickup' ? 'Store Pickup' : 'Ship' }
      })
    }).catch(function () {});
  }

  /* ── Apply mode — class-based, no hidden attribute ── */
  function applyMode(root, mode) {
    var btnShip   = root.querySelector('.fa-fulfil__btn--ship');
    var btnPickup = root.querySelector('.fa-fulfil__btn--pickup');
    var shipPanel = root.querySelector('.fa-fulfil__ship-panel');
    var pickPanel = root.querySelector('.fa-fulfil__pickup-panel');

    if (!btnShip || !btnPickup) return;

    var isShip = mode === 'ship';

    /* Toggle buttons (mobile) */
    btnShip.classList.toggle('is-active', isShip);
    btnPickup.classList.toggle('is-active', !isShip);
    btnShip.setAttribute('aria-pressed', isShip ? 'true' : 'false');
    btnPickup.setAttribute('aria-pressed', !isShip ? 'true' : 'false');

    /* Toggle panel visibility (mobile animation) */
    if (shipPanel) {
      shipPanel.classList.toggle('fa-fulfil__panel--hidden', !isShip);
      shipPanel.setAttribute('aria-hidden', !isShip ? 'true' : 'false');
    }
    if (pickPanel) {
      pickPanel.classList.toggle('fa-fulfil__panel--hidden', isShip);
      pickPanel.setAttribute('aria-hidden', isShip ? 'true' : 'false');
    }

    /* Desktop: toggle is-selected on the card boxes */
    if (shipPanel) shipPanel.classList.toggle('is-selected', isShip);
    if (pickPanel) pickPanel.classList.toggle('is-selected', !isShip);

    /* Sync cart summary selector */
    if (!root.closest('#fa-cart-summary')) {
      var cartSelector = document.querySelector('#fa-cart-summary .fa-fulfil-selector');
      if (cartSelector) applyMode(cartSelector, mode);
    }

    /* Cart shipping row */
    var shippingRow = document.getElementById('fa-shipping-row');
    if (shippingRow) shippingRow.style.display = isShip ? '' : 'none';
  }

  /* ── ZIP shipping calculator ── */
  function calculateShipping(root) {
    var zipInput   = root.querySelector('.fa-fulfil__zip-input');
    var stateInput = root.querySelector('.fa-fulfil__zip-state');
    var resultEl   = root.querySelector('.fa-fulfil__zip-result');
    var btn        = root.querySelector('.fa-fulfil__zip-btn');
    var shippingVal = document.getElementById('fa-shipping-val');

    var zip   = zipInput   ? zipInput.value.trim() : '';
    var state = stateInput ? stateInput.value      : '';

    if (!zip)   { showResult(resultEl, 'Please enter a ZIP code.', 'is-error'); return; }
    if (!state) { showResult(resultEl, 'Please select your state.', 'is-error'); return; }

    btn.classList.add('is-loading');
    btn.textContent = '...';
    showResult(resultEl, 'Calculating\u2026', '');

    try { localStorage.setItem(LS_ZIP, zip); localStorage.setItem(LS_STATE, state); } catch (e) {}

    var url = '/cart/shipping_rates.json'
      + '?shipping_address[zip]='      + encodeURIComponent(zip)
      + '&shipping_address[country]=US'
      + '&shipping_address[province]=' + encodeURIComponent(state);

    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var rates = data.shipping_rates;
        if (!rates || !rates.length) {
          showResult(resultEl, 'No rates found for this address.', 'is-error');
          if (shippingVal) { shippingVal.textContent = 'Unavailable'; shippingVal.className = 'fa-summary__totals-val'; }
          return;
        }
        rates.sort(function (a, b) { return parseFloat(a.price) - parseFloat(b.price); });
        var price = parseFloat(rates[0].price);
        if (price === 0) {
          showResult(resultEl, '\u2713 Free shipping to ' + zip + ', ' + state, 'is-free');
          if (shippingVal) { shippingVal.textContent = 'Free'; shippingVal.className = 'fa-summary__totals-val is-free'; }
        } else {
          var allRates = rates.map(function (r) {
            return r.name + ' \u00b7 $' + parseFloat(r.price).toFixed(2);
          }).join('  \u2022  ');
          showResult(resultEl, allRates, '');
          if (shippingVal) { shippingVal.textContent = 'from $' + price.toFixed(2); shippingVal.className = 'fa-summary__totals-val is-calculated'; }
        }
      })
      .catch(function () {
        showResult(resultEl, 'Could not calculate. Please try again.', 'is-error');
      })
      .finally(function () {
        btn.classList.remove('is-loading');
        btn.textContent = 'Calculate';
      });
  }

  function showResult(el, text, cls) {
    if (!el) return;
    el.textContent = text;
    el.className = 'fa-fulfil__zip-result' + (cls ? ' ' + cls : '');
  }

  /* ── Wire up a selector root ── */
  function wireSelector(root) {
    var btnShip   = root.querySelector('.fa-fulfil__btn--ship');
    var btnPickup = root.querySelector('.fa-fulfil__btn--pickup');
    var zipCalc   = root.querySelector('.fa-fulfil__zip-btn');
    var zipInput  = root.querySelector('.fa-fulfil__zip-input');
    var shipPanel = root.querySelector('.fa-fulfil__ship-panel');
    var pickPanel = root.querySelector('.fa-fulfil__pickup-panel');

    if (!btnShip) return;

    /* Mobile toggle buttons */
    btnShip.addEventListener('click', function () {
      save('ship');
      applyMode(root, 'ship');
      updateCartAttribute('ship');
    });

    btnPickup.addEventListener('click', function () {
      save('pickup');
      applyMode(root, 'pickup');
      updateCartAttribute('pickup');
    });

    /* Desktop: clicking the card selects it + carries over to cart */
    if (shipPanel) {
      shipPanel.addEventListener('click', function (e) {
        /* Ignore clicks inside the ZIP form so typing doesn't re-trigger */
        if (e.target.closest('.fa-fulfil__zip-form')) return;
        save('ship');
        applyMode(root, 'ship');
        updateCartAttribute('ship');
      });
    }

    if (pickPanel) {
      pickPanel.addEventListener('click', function () {
        save('pickup');
        applyMode(root, 'pickup');
        updateCartAttribute('pickup');
      });
    }

    if (zipCalc) {
      zipCalc.addEventListener('click', function () { calculateShipping(root); });
    }

    if (zipInput) {
      zipInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') calculateShipping(root);
      });
    }

    /* Restore saved ZIP + state */
    try {
      var savedZip   = localStorage.getItem(LS_ZIP);
      var savedState = localStorage.getItem(LS_STATE);
      if (savedZip   && zipInput) zipInput.value = savedZip;
      if (savedState) {
        var stateEl = root.querySelector('.fa-fulfil__zip-state');
        if (stateEl) stateEl.value = savedState;
      }
    } catch (e) {}
  }

  /* ── Init ── */
  document.addEventListener('DOMContentLoaded', function () {
    var selectors = document.querySelectorAll('.fa-fulfil-selector');
    if (!selectors.length) return;

    /* Parse the RFMS inventory JSON written by the Liquid (may be absent) */
    var dataEl = document.getElementById('fa-inventory-data');
    if (dataEl) {
      try { invData = JSON.parse(dataEl.textContent); } catch (e) { invData = null; }
    }

    var savedMode = getSaved();

    /* ── Pickup-only mode ──
       Liquid puts data-pickup-only on the wrapper when
       settings.fa_pickup_only is checked. Force the mode to pickup so a
       stale "ship" in localStorage can't leak into cart attributes, and
       hide the cart shipping row (applyMode bails out in this mode
       because the toggle buttons aren't rendered). */
    var pickupOnly = !!document.querySelector('.fa-fulfil-selector[data-pickup-only]');
    if (pickupOnly) {
      savedMode = 'pickup';
      save('pickup');
      var pickupShipRow = document.getElementById('fa-shipping-row');
      if (pickupShipRow) pickupShipRow.style.display = 'none';
    }

    selectors.forEach(function (root) {
      wireSelector(root);
      applyMode(root, savedMode);
    });

    /* Inventory badge from Liquid data attribute + RFMS metafield map */
    var box = document.querySelector('.fa-pdp__fulfil-box');
    if (box) {
      var initialVariant = invData && invData.initial_variant != null ? invData.initial_variant : null;
      updateInventoryBadge(parseInt(box.getAttribute('data-inventory')) || 0, initialVariant);
    }
    document.addEventListener('fa:variant-changed', function (e) {
      var d = e.detail || {};
      /* Accept whichever id field the dispatcher provides */
      var vid = d.variant_id != null ? d.variant_id
              : d.variantId  != null ? d.variantId
              : d.id         != null ? d.id
              : null;
      updateInventoryBadge(d.inventory_quantity || 0, vid);
    });

    /* Auto-calc ZIP on cart page if saved */
    if (savedMode === 'ship' && window.location.pathname === '/cart') {
      try {
        var z = localStorage.getItem(LS_ZIP);
        var s = localStorage.getItem(LS_STATE);
        if (z && s) {
          var first = document.querySelector('.fa-fulfil-selector');
          if (first) {
            var zi = first.querySelector('.fa-fulfil__zip-input');
            var si = first.querySelector('.fa-fulfil__zip-state');
            if (zi) zi.value = z;
            if (si) si.value = s;
            calculateShipping(first);
          }
        }
      } catch (e) {}
    }

    if (window.location.pathname === '/cart') {
      updateCartAttribute(savedMode);
    }
  });

  window.FAFulfillment = {
    setMode: function (mode) {
      save(mode);
      updateCartAttribute(mode);
      document.querySelectorAll('.fa-fulfil-selector').forEach(function (root) {
        applyMode(root, mode);
      });
    }
  };
})();
